'use client';

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

/**
 * Drives the draggable split between the sidebar's nav/menu block and its
 * Recents (session history) list.
 *
 * The two panes are flex children that share the sidebar's leftover vertical
 * space via `flexGrow`, so the ratio below is the *proportion of that shared
 * space* the menu gets — not a proportion of the sidebar. Everything else in
 * the rail (header, separator, Recents label, footer) is `shrink-0` and sits
 * outside the split, which is why the drag math measures the two panes rather
 * than the container.
 */

/** Even split — the menu and the history list start with equal space. */
export const DEFAULT_MENU_RATIO = 0.5;

/**
 * Neither pane may be dragged below this. A pane thinner than roughly two rows
 * is not a smaller pane, it is a broken one: the scroll container still exists
 * and still steals wheel events, so the user gets a dead strip they cannot
 * read and cannot easily drag back open.
 */
export const MIN_PANE_PX = 96;

/** Arrow-key step, in ratio units (~2% of the flexible space per press). */
const KEYBOARD_STEP = 0.02;

const STORAGE_KEY = 'lexomat:sidebar-split-ratio:v1';

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Clamp a raw ratio so both panes keep {@link MIN_PANE_PX}.
 *
 * `flexiblePx` is the combined height of the two panes. When the viewport is so
 * short that the two floors cannot both fit, there is no honest answer — we
 * clamp to the plain 0–1 range and let CSS decide, rather than snapping the
 * split somewhere the user did not ask for.
 */
export function clampMenuRatio(rawRatio: number, flexiblePx: number): number {
  if (!Number.isFinite(rawRatio) || flexiblePx <= 0) return DEFAULT_MENU_RATIO;

  const minRatio = MIN_PANE_PX / flexiblePx;
  const maxRatio = 1 - minRatio;
  // `>`, not `>=`: at EXACTLY two floors the two bounds meet at 0.5, which is a
  // legal split (both panes land on their floor) — giving up there would leave
  // the ratio wherever it happened to be and under-fill one of the panes.
  if (minRatio > maxRatio) return clamp(rawRatio, 0, 1);

  return clamp(rawRatio, minRatio, maxRatio);
}

function readStoredRatio(): number | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed = Number.parseFloat(raw);
    // Reject anything that is not a usable fraction — a corrupted or
    // hand-edited value must fall back to the default, never render a
    // zero-height pane.
    if (!Number.isFinite(parsed) || parsed <= 0 || parsed >= 1) return null;
    return parsed;
  } catch {
    // Private mode / blocked site data / disabled storage. The split is a
    // convenience, so losing it is not worth failing a render over.
    return null;
  }
}

function writeStoredRatio(ratio: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(ratio));
  } catch {
    // See readStoredRatio — best effort only.
  }
}

/** Not exported: the hook annotates its own return, nothing else names it. */
interface UseSidebarSplitResult {
  /** Share of the flexible space given to the menu pane (0–1). */
  menuRatio: number;
  /** Attach to the nav/menu pane. */
  menuRef: RefObject<HTMLDivElement | null>;
  /** Attach to the Recents pane. */
  recentsRef: RefObject<HTMLDivElement | null>;
  /** True while a pointer drag is in progress (for cursor / hover styling). */
  isDragging: boolean;
  /** Attach to the separator's `onMouseDown`. */
  onSeparatorMouseDown: (event: ReactMouseEvent) => void;
  /** Attach to the separator's `onKeyDown` — arrows, Home/End, Enter to reset. */
  onSeparatorKeyDown: (event: ReactKeyboardEvent) => void;
  /** Restore the even split (double-click the separator). */
  resetSplit: () => void;
}

export function useSidebarSplit(): UseSidebarSplitResult {
  const [menuRatio, setMenuRatio] = useState(DEFAULT_MENU_RATIO);
  const [isDragging, setIsDragging] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const recentsRef = useRef<HTMLDivElement | null>(null);

  // Restore after mount, never during render: the server has no localStorage,
  // so reading it inline would hydrate a different tree than the server sent.
  useEffect(() => {
    const stored = readStoredRatio();
    if (stored !== null) setMenuRatio(stored);
  }, []);

  /** Combined height of the two panes — the space the ratio divides. */
  const measureFlexiblePx = useCallback((): number => {
    const menuEl = menuRef.current;
    const recentsEl = recentsRef.current;
    if (!menuEl || !recentsEl) return 0;
    return menuEl.offsetHeight + recentsEl.offsetHeight;
  }, []);

  const commitRatio = useCallback((next: number) => {
    setMenuRatio(next);
    writeStoredRatio(next);
  }, []);

  /** Move the split to the pointer. Returns the ratio applied, or null if the
   *  panes could not be measured (nothing rendered yet). */
  const applyPointerRatio = useCallback(
    (clientY: number): number | null => {
      const menuEl = menuRef.current;
      if (!menuEl) return null;

      const flexiblePx = measureFlexiblePx();
      if (flexiblePx <= 0) return null;

      // The menu pane's top edge is the split's origin; the pointer's distance
      // from it is the height the user is asking the menu to have.
      const desiredMenuPx = clientY - menuEl.getBoundingClientRect().top;
      const next = clampMenuRatio(desiredMenuPx / flexiblePx, flexiblePx);
      setMenuRatio(next);
      return next;
    },
    [measureFlexiblePx]
  );

  /**
   * Whether the pointer actually moved during the current gesture.
   *
   * A press that never moves must leave the split alone. Without this, merely
   * CLICKING the handle snaps the boundary to the click point — and because
   * that jump moves the handle out from under the pointer, the second click of
   * a double-click lands somewhere else, no `dblclick` is generated, and the
   * double-click-to-reset affordance silently never fires.
   */
  const didMoveRef = useRef(false);

  const onSeparatorMouseDown = useCallback((event: ReactMouseEvent) => {
    // Stop the browser starting a text selection the moment the drag begins.
    event.preventDefault();
    didMoveRef.current = false;
    setIsDragging(true);
  }, []);

  const resetSplit = useCallback(() => {
    commitRatio(DEFAULT_MENU_RATIO);
  }, [commitRatio]);

  const onSeparatorKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      const flexiblePx = measureFlexiblePx();

      // A keyboard user gets the same range a mouse user does, including the
      // floors — so the separator can never be driven into a dead strip.
      const step = (delta: number) => {
        event.preventDefault();
        commitRatio(clampMenuRatio(menuRatio + delta, flexiblePx));
      };

      switch (event.key) {
        case 'ArrowUp':
          step(-KEYBOARD_STEP);
          break;
        case 'ArrowDown':
          step(KEYBOARD_STEP);
          break;
        case 'Home':
          // Smallest legal menu pane (history gets the most room).
          step(-1);
          break;
        case 'End':
          step(1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          resetSplit();
          break;
        default:
          break;
      }
    },
    [commitRatio, measureFlexiblePx, menuRatio, resetSplit]
  );

  // Drag lifecycle. Listeners go on `document` so the split keeps tracking the
  // pointer once it leaves the 8px separator, which is most of any real drag.
  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (event: MouseEvent) => {
      didMoveRef.current = true;
      applyPointerRatio(event.clientY);
    };
    const handleUp = (event: MouseEvent) => {
      // Only a gesture that moved changes the split — see didMoveRef. Persist
      // here, at the end of the gesture, rather than on every mousemove: a
      // drag fires dozens of updates and localStorage writes are synchronous.
      if (didMoveRef.current) {
        const settled = applyPointerRatio(event.clientY);
        if (settled !== null) writeStoredRatio(settled);
      }
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);

    // Suppress selection and pin the resize cursor for the whole gesture —
    // without this the cursor flickers to a text caret over every label the
    // pointer crosses.
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'row-resize';

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isDragging, applyPointerRatio]);

  return {
    menuRatio,
    menuRef,
    recentsRef,
    isDragging,
    onSeparatorMouseDown,
    onSeparatorKeyDown,
    resetSplit,
  };
}
